import React, { Component } from 'react'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'

import uiBox, { DecoratorProps } from 'client/shared/decorators/ui-box'
import withFixed, { FixedProps } from 'client/shared/decorators/with-fixed'

import urlFor from 'shared/tools/url-helper'
import bookHelper from 'shared/tools/book-helper'
import audiobookHelper from 'shared/tools/audiobook-helper'
import comicbookHelper from 'shared/tools/comicbook-helper'
import { resourceTypes } from 'client/shared/helpers/resource-helpers'

import { show } from 'client/shared/reducers/fake-page-reducer'

import Linka from 'client/shared/blocks/linka'
import DeeplinkButton from 'client/shared/blocks/deeplink-button'
import PlusBox from 'client/shared/boxes/plus-box'
import FloatingAction from 'client/shared/blocks/floating-action'

import { ResourceProps } from 'client/shared/types/resource'
import { ShortSeriesProps } from 'client/shared/types/series'
import { Exact } from 'types/local/exact-check'

import { withRouter } from 'react-router'
import { Location, Router } from 'client/shared/types/react-router'
import './add-button.styl'
import {
  analyticsEvent,
  CONTENT_ADD,
  CONTENT_READ,
} from 'client/shared/reducers/analytics-reducer'
import { getCurrentDateTime } from 'shared/tools/date-helper'

const { BOOK, AUDIOBOOK, COMICBOOK, SERIAL } = resourceTypes

type Props = Exact<DecoratorProps> &
  Exact<withI18nProps> &
  Exact<FixedProps> & {
    resource: ResourceProps
    text: string
    stage: string
    disablePlus: boolean
    fixed: boolean
    series?: ShortSeriesProps
    serialInfo?: Record<string, string | number>
    domain: string
    location: Location
    userId: number
    router: Router
  }

export const READ_BUTTON = 'read-button'
export const ADD_PLUS = 'add-button-plus'
export const TRACED_IDS = [READ_BUTTON, ADD_PLUS]

class AddButton extends Component<Props> {
  getType() {
    const { resource } = this.props
    return resource.resourceType === 'book' &&
      resource.restricted_reading_on_web
      ? 'book_read_in_app_only'
      : resource.resourceType
  }

  getButtonText() {
    const { i18n } = this.props

    switch (this.getType()) {
      case BOOK:
        return i18n.t`buttons.read`
      case 'book_read_in_app_only':
      case COMICBOOK:
        return i18n.t`buttons.read_in_app`
      case AUDIOBOOK:
        return i18n.t`buttons.listen_in_app`
      case SERIAL:
        return i18n.t`buttons.read`
      default:
        return i18n.t`buttons.open_in_app`
    }
  }

  shouldShowPlus() {
    const { auth, isMobileOS, disablePlus } = this.props

    if (disablePlus) return false

    return (isMobileOS() && auth) || !isMobileOS()
  }

  isAdded() {
    const { resource } = this.props

    switch (resource.resourceType) {
      case SERIAL:
      case BOOK:
        return bookHelper.isAdded(resource)
      case COMICBOOK:
        return comicbookHelper.isAdded(resource)
      case AUDIOBOOK:
        return audiobookHelper.isAdded(resource)
    }
  }

  dispatchAnalyticsEvent(id: string): void {
    if (
      !TRACED_IDS.includes(id) ||
      this.props.resource.library_card ||
      this.props.resource.audio_card
    )
      return

    const {
      dispatch,
      userId,
      resource: { resourceType, uuid },
    } = this.props

    const dateTime = getCurrentDateTime()
    const eventType = id === READ_BUTTON ? CONTENT_READ : CONTENT_ADD

    dispatch(
      analyticsEvent(eventType, {
        event_datetime: dateTime,
        user_id: userId,
        screen: `page_${resourceType}`,
        screen_uuid: uuid,
        content_type: resourceType,
        content_uuid: uuid,
        content_position: 1,
      }),
    )
  }

  render() {
    const { resource, isMobileOS, stage, renderScroller, isFixed } = this.props
    const { resourceType } = resource

    const addedMod = this.isAdded() ? 'add-button_added' : ''

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!resource.can_be_read && !resource.can_be_listened) return null

    return (
      <div
        className={`add-button ${addedMod}`}
        onClick={event => this.dispatchAnalyticsEvent(event.target?.id || '')}
      >
        {isMobileOS() && renderScroller()}
        {isMobileOS() ? (
          <DeeplinkButton
            resourceType={resourceType}
            uuid={resource.uuid}
            campaign={
              resourceType === AUDIOBOOK
                ? 'install_to_listen'
                : 'install_to_read'
            }
            stage={stage}
          />
        ) : (
          this.renderAddButton()
        )}
        {this.shouldShowPlus() && (
          <span id="add-button-plus" className="add-button__icon">
            {this.renderPlus()}
          </span>
        )}

        {isMobileOS() && isFixed() && this.renderFloatingAction()}
      </div>
    )
  }

  renderAddButton() {
    const { resource, dispatch, serialInfo = {}, series, router } = this.props
    const type = this.getType()
    const buttonText = this.getButtonText()

    switch (type) {
      case BOOK:
        return (
          <Linka
            id="read-button"
            className="add-button__link"
            path={urlFor.reader(
              resource.uuid,
              {
                resourceType: 'book',
                seriesUuid: (series || {}).uuid,
              },
              router?.location?.query,
            )}
          >
            {buttonText}
          </Linka>
        )
      case SERIAL:
        return (
          <Linka
            id="read-button"
            className="add-button__link"
            path={urlFor.reader(
              resource.uuid,
              {
                resourceType: 'serial',
              },
              { ...serialInfo, ...router?.location?.query },
            )}
          >
            {buttonText}
          </Linka>
        )
      case 'book_read_in_app_only':
      case COMICBOOK:
      case AUDIOBOOK:
      default:
        return (
          <span
            id="read-button"
            className="add-button__link"
            onClick={() => dispatch(show('getTheApp', { type }))}
          >
            {buttonText}
          </span>
        )
    }
  }

  renderPlus() {
    const { resource } = this.props
    if (resource.resourceType === BOOK || resource.resourceType === SERIAL) {
      return <PlusBox book={resource} stateless />
    } else if (resource.resourceType === AUDIOBOOK) {
      return <PlusBox audiobook={resource} stateless />
    } else if (resource.resourceType === COMICBOOK) {
      return <PlusBox comicbook={resource} stateless />
    }
  }

  renderFloatingAction() {
    const { resource, stage } = this.props
    const { resourceType } = resource

    return (
      <FloatingAction>
        <DeeplinkButton
          resourceType={resourceType}
          uuid={resource.uuid}
          campaign={
            resourceType === AUDIOBOOK ? 'install_to_listen' : 'install_to_read'
          }
          stage={stage}
        />
      </FloatingAction>
    )
  }
}

const wrapper = compose(
  uiBox,
  withFixed,
  withI18n({ update: true }),
  withRouter,
)

export default wrapper(AddButton)
